import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from './AuthContext';
import { PlanType } from '../services/paymentService';

// 추가: 사용자 일일 사용량 상세 정보 타입
interface UserDailyUsage {
  daily_draft_plans_used: number;
  last_draft_plan_usage_date: string | null;
  daily_startup_templates_used: number;
  last_startup_template_usage_date: string | null;
}

interface SubscriptionContextType {
  currentPlan: PlanType;
  isLoading: boolean;
  canUseFeature: (feature: Feature) => boolean;
  remainingUsage: {
    aiBusinessPlans: number;
    specializedTemplates: number;
  };
  userDailyUsage: UserDailyUsage | null;
  refreshSubscription: () => Promise<void>;
}

// Define all features that can be limited by subscription
export type Feature = 
  | 'advancedSearch'
  | 'unlimitedNotifications'
  | 'unlimitedAiPlans'
  | 'specializedTemplates'
  | 'documentConversion'
  | 'allTemplates'
  | 'expertConsulting'
  | 'prioritySupport';

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider = ({ children }: SubscriptionProviderProps) => {
  const [currentPlan, setCurrentPlan] = useState<PlanType>('free');
  const [isLoading, setIsLoading] = useState(true);
  const [remainingUsage, setRemainingUsage] = useState({
    aiBusinessPlans: 1,
    specializedTemplates: 0,
  });
  const [userDailyUsage, setUserDailyUsage] = useState<UserDailyUsage | null>(null);
  
  const { user, loading: authLoading } = useAuth();
  const isFetching = useRef(false);

  // Feature mapping to plans
  const featureAvailability: Record<Feature, PlanType[]> = {
    advancedSearch: ['pro'],
    unlimitedNotifications: ['pro'],
    unlimitedAiPlans: ['pro'],
    specializedTemplates: ['pro'],
    documentConversion: ['pro'],
    allTemplates: ['pro'],
    expertConsulting: ['pro'],
    prioritySupport: ['pro']
  };
  
  // Check if a feature is available for the current plan
  const canUseFeature = useCallback((feature: Feature): boolean => {
    if (isLoading) return false;
    if (!featureAvailability[feature]) {
      return false;
    }
    return featureAvailability[feature].includes(currentPlan);
  }, [currentPlan, isLoading]);

  const fetchSubscription = useCallback(async (userId: string) => {
    if (isFetching.current) return;
    isFetching.current = true;
    setIsLoading(true);

    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('plan_type, daily_draft_plans_used, last_draft_plan_usage_date, daily_startup_templates_used, last_startup_template_usage_date')
        .eq('id', userId)
        .single();
      
      if (userError) throw userError;
      
      if (userData) {
        const plan = (userData.plan_type as PlanType) || 'free';
        setCurrentPlan(plan);
        setUserDailyUsage({
          daily_draft_plans_used: userData.daily_draft_plans_used ?? 0,
          last_draft_plan_usage_date: userData.last_draft_plan_usage_date,
          daily_startup_templates_used: userData.daily_startup_templates_used ?? 0,
          last_startup_template_usage_date: userData.last_startup_template_usage_date,
        });

        const isPro = plan === 'pro';
        setRemainingUsage({
          aiBusinessPlans: isPro ? -1 : 1,
          specializedTemplates: isPro ? -1 : 0,
        });
      }
    } catch (error) {
      console.error('Error fetching user subscription:', error);
      setCurrentPlan('free');
      setRemainingUsage({ aiBusinessPlans: 1, specializedTemplates: 0 });
      setUserDailyUsage(null);
    } finally {
      setIsLoading(false);
      isFetching.current = false;
    }
  }, []);

  const resetToFree = () => {
    setCurrentPlan('free');
    setRemainingUsage({ aiBusinessPlans: 1, specializedTemplates: 0 });
    setUserDailyUsage(null);
    setIsLoading(false);
  }

  // Refresh subscription data
  const refreshSubscription = useCallback(async () => {
    if (user?.id) {
      await fetchSubscription(user.id);
    }
  }, [user?.id, fetchSubscription]);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (user) {
      fetchSubscription(user.id);
    } else {
      resetToFree();
    }
  }, [user, authLoading, fetchSubscription]);

  const contextValue = {
    currentPlan,
    isLoading,
    canUseFeature,
    remainingUsage,
    userDailyUsage,
    refreshSubscription
  };

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
}; 